/** Per-session run controllers. Transport is one multiplexed SSE connection. */

const controllers = new Map<string, AbortController>();

function abortKey(key: string) {
  controllers.get(key)?.abort();
  controllers.delete(key);
}

export function beginWorkflowStream(sessionKey?: string | null): AbortController {
  const key = sessionKey?.trim() || "__draft__";
  abortKey(key);
  const next = new AbortController();
  controllers.set(key, next);
  return next;
}

export function rekeyWorkflowStream(
  fromKey: string,
  toKey: string,
  controller: AbortController,
): void {
  const from = fromKey.trim() || "__draft__";
  const to = toKey.trim();
  if (!to || from === to) return;
  if (controllers.get(from) === controller) controllers.delete(from);
  const existing = controllers.get(to);
  if (existing && existing !== controller) existing.abort();
  controllers.set(to, controller);
}

/** Abort one session, or every stream if no id is passed. */
export function abortActiveWorkflowStream(sessionId?: string | null): void {
  if (sessionId?.trim()) {
    abortKey(sessionId.trim());
    return;
  }
  for (const controller of controllers.values()) controller.abort();
  controllers.clear();
}

export function clearActiveWorkflowStream(
  active: AbortController,
  sessionKey?: string | null,
): void {
  if (sessionKey?.trim()) {
    const key = sessionKey.trim();
    if (controllers.get(key) === active) controllers.delete(key);
    return;
  }
  for (const [key, controller] of controllers) {
    if (controller === active) controllers.delete(key);
  }
}

export function isWorkflowStreamActive(sessionId?: string | null): boolean {
  if (sessionId?.trim()) return controllers.has(sessionId.trim());
  return controllers.size > 0;
}

export function listActiveWorkflowStreamSessions(): string[] {
  return [...controllers.keys()].filter((key) => key !== "__draft__");
}

export function isAbortError(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === "AbortError";
}
